// ===========================================
// Messages API Functions
// ===========================================
// API calls for messaging endpoints

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export type UserRole = 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT';

export interface Message {
  id: string;
  content: string;
  senderId: string;
  readAt: string | null;
  createdAt: string;
  sender: {
    id: string;
    firstName: string;
    lastName: string;
    color?: string | null;
  };
}

export interface Conversation {
  id: string;
  participant1Id: string;
  participant2Id: string;
  lastMessageAt: string | null;
  createdAt: string;
  otherParticipant: {
    id: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    /** The school office (an ADMIN that is not a support account, or staff holding messages.write): labelled as the office, not by role. */
    isOffice?: boolean;
    /** What the chip says (the server's one rule): "School office", the owner title, "Support" or the role. */
    label?: string;
    color?: string | null; // Teacher color (hex)
    avatar?: string | null; // Student avatar path
    birthDate?: string | null; // Student birth date (for default avatar)
  };
  unreadCount: number;
  lastMessage?: {
    content: string;
    createdAt: string;
    senderId: string;
  };
}

/** Why a non-office user may message this recipient: the classes that link them (#326). */
export interface RecipientRelationship {
  /** A teacher or student: instrument (else class) names of the classes that link them. */
  instruments?: string[];
  /** A parent (teacher caller): first names of their children in the caller's classes. */
  children?: string[];
}

export interface Recipient {
  id: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  /** Sent to the office only: parents and teachers never see each other's email (#326). */
  email?: string;
  /** Sent to everyone else; absent for the office and for teacher→teacher. */
  relationship?: RecipientRelationship;
  /** The school office (an Admin that is not a support account, or staff who handle messages): grouped as the office, whatever their role. */
  isOffice: boolean;
  /** What the chip says (the server's one rule): "School office", the owner title, "Support" or the role. */
  label?: string;
}

export interface SendMessageInput {
  recipientId: string;
  content: string;
}

export interface GetMessagesParams {
  limit?: number;
  offset?: number;
}

export interface GetMessagesResponse {
  messages: Message[];
  total: number;
}

export interface UnreadCountResponse {
  count: number;
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

/**
 * Get display name for user role
 */
/** The label the school office is shown under in Messages (mirrors the backend OFFICE_DISPLAY_NAME). */
export const OFFICE_GROUP_LABEL = 'School office';

/**
 * What a person's chip says in Messages. The server decides (roles slice 2): the office reads as
 * "School office", or an Admin as the owner title ("Principal") when the school chooses it; a
 * support account as "Support" (or "Teacher" when it teaches). The fallback covers a row without it.
 */
export function messagingLabel(p: { role: UserRole; isOffice?: boolean; label?: string }): string {
  return p.label ?? (p.isOffice ? OFFICE_GROUP_LABEL : getRoleDisplayName(p.role));
}

export function getRoleDisplayName(role: UserRole): string {
  const roleNames: Record<UserRole, string> = {
    ADMIN: 'Administrator',
    TEACHER: 'Teacher',
    PARENT: 'Parent',
    STUDENT: 'Student',
  };
  return roleNames[role] || role;
}

/** Shows at most `max` names, then "+N more". */
function capList(names: string[], max = 3): { shown: string[]; more: number } {
  return { shown: names.slice(0, max), more: Math.max(0, names.length - max) };
}

/**
 * The line under a recipient's name in the New Message picker (#326): the email for an office
 * user (the only caller the server sends one to), otherwise who the person is to the caller,
 * e.g. "Teacher · Piano" or "Parent of Emma and Jack". Never a contact detail.
 */
export function recipientSubtitle(r: Recipient): string {
  if (r.email) return r.email;
  if (r.isOffice) return OFFICE_GROUP_LABEL;
  const role = getRoleDisplayName(r.role);
  const children = r.relationship?.children ?? [];
  if (r.role === 'PARENT' && children.length > 0) {
    const { shown, more } = capList(children);
    const names = more > 0 ? `${shown.join(', ')} +${more} more` : formatNameList(shown);
    return `Parent of ${names}`;
  }
  const instruments = r.relationship?.instruments ?? [];
  if (instruments.length > 0) {
    const { shown, more } = capList(instruments);
    return `${role} · ${shown.join(', ')}${more > 0 ? ` +${more} more` : ''}`;
  }
  return role;
}

/** "Emma", "Emma and Jack", "Emma, Jack and Mia" (en-AU, no Oxford comma). */
function formatNameList(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Get color for user role (for badges/chips)
 */
export function getRoleColor(
  role: UserRole
): 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error' | 'default' {
  const roleColors: Record<
    UserRole,
    'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error' | 'default'
  > = {
    ADMIN: 'error',
    TEACHER: 'primary',
    PARENT: 'success',
    STUDENT: 'info',
  };
  return roleColors[role] || 'default';
}

// ===========================================
// MESSAGES API
// ===========================================

export const messagesApi = {
  // Get all conversations for the current user
  getConversations: (): Promise<Conversation[]> =>
    apiClient
      .get<{ status: string; data: Conversation[] }>('/messages/conversations')
      .then((res) => res.data),

  // Get messages for a specific conversation
  getConversationMessages: (
    conversationId: string,
    params?: GetMessagesParams
  ): Promise<GetMessagesResponse> =>
    apiClient
      .get<{ status: string; data: GetMessagesResponse }>(
        `/messages/conversations/${conversationId}`,
        { params }
      )
      .then((res) => res.data),

  // Send a new message
  sendMessage: (data: SendMessageInput): Promise<Message> =>
    apiClient
      .post<{ status: string; data: Message }>('/messages', data)
      .then((res) => res.data),

  // Broadcast a message to class members (teachers/admins only)
  classBroadcast: (data: { lessonId: string; recipientIds: string[]; content: string }): Promise<{ sent: number; failed: number }> =>
    apiClient
      .post<{ status: string; data: { sent: number; failed: number } }>('/messages/class-broadcast', data)
      .then((res) => res.data),

  /**
   * Broadcast a message to members of ONE family.
   * Same `{ sent, failed }` shape as `classBroadcast` — a partial success is a real
   * outcome, so the caller reports counts rather than a single pass/fail.
   */
  familyBroadcast: (data: { familyId: string; recipientIds: string[]; content: string }): Promise<{ sent: number; failed: number }> =>
    apiClient
      .post<{ status: string; data: { sent: number; failed: number } }>('/messages/family-broadcast', data)
      .then((res) => res.data),

  // Mark a conversation as read
  markAsRead: (conversationId: string): Promise<void> =>
    apiClient
      .patch<{ status: string; message: string }>(
        `/messages/conversations/${conversationId}/read`
      )
      .then(() => undefined),

  // Get list of valid recipients
  getRecipients: (): Promise<Recipient[]> =>
    apiClient
      .get<{ status: string; data: Recipient[] }>('/messages/recipients')
      .then((res) => res.data),

  // Get unread message count
  getUnreadCount: (): Promise<UnreadCountResponse> =>
    apiClient
      .get<{ status: string; data: UnreadCountResponse }>('/messages/unread-count')
      .then((res) => res.data),
};
